import * as d3 from 'd3';
import Utils from "../Utility/Utils";
import CountryScoreCard from './CountryScoreCard';
import SubPage from '../SubPage';
import { event } from 'jquery';
import MobileCountryDetailSlider from './mobileClasses/CountrySwiperDetail';

export default class WorldMap {
    constructor(containerId, countryData) {
        this.wrapper = document.getElementById('world_svg_map_container');
        this.container = document.getElementById(containerId);
        this.mapContainer = this.container.querySelector('.map_container');
        this.wrapper.style.opacity = '0';
        this.wrapper.style.visibility = 'hidden';
        this.wrapper.style.pointerEvents = 'none';
        this.width = this.container.offsetWidth;
        this.height = this.container.offsetHeight;
        this.countryData = countryData;
        this.countryScoreCard = CountryScoreCard.getInstance(countryData);

        // Generate regions dynamically
        this.generateRegions();

        // Set up SVG element
        this.svg = d3.select(this.mapContainer)
            .append('svg')
            .attr('width', this.width)
            .attr('height', this.height)
            .on('wheel.zoom', null); // Disable mouse scroll zoom

        // Initialize zoom behavior first
        this.setupZoomBehavior();

        // Create dotted pattern for regions
        this.setupDottedPattern();
        this.projection = d3.geoMercator().scale(150).translate([this.width / 2, this.height / 1.5]);
        this.path = d3.geoPath().projection(this.projection);

        this.g = this.svg.append('g');
        this.currentTransform = d3.zoomIdentity;
        this.isPanning = false;

        this.customShades = ["#E6F3FF", "#CDE7FE", "#9ACFFE", "#68B7FD", "#359FFD", "#026CCA"];
        this.strokeShades = ["#026CCA", "#359FFD", "#68B7FD", "#9ACFFE", "#CDE7FE", "#E6F3FF"];

        

        // Load GeoJSON data
        this.loadData();
        this.handleResize();
    }

    // Setup zoom behavior
    setupZoomBehavior() {
        this.zoomBehavior = d3.zoom()
            .scaleExtent([2.5, 8])
            .translateExtent([[0, 0], [this.width * 2, this.height * 2]])
            .on('zoom', (event) => {
                // Check if g and dotted are defined before applying transformation
                if (this.g && this.dotted) {
                    this.g.attr('transform', event.transform);
                    this.dotted.attr('transform', event.transform);
                }
            });

        // Ensure that zoomBehavior is set before using .call
        if (this.svg) {
            this.svg.call(this.zoomBehavior);
        } else {
            console.error('SVG element is not defined when setting up zoom behavior.');
        }
    }

    // Create dotted pattern for regions
    setupDottedPattern() {
        const defs = this.svg.append('defs');
        defs.append('pattern')
            .attr('id', 'dotted-pattern')
            .attr('patternUnits', 'userSpaceOnUse')
            .attr('width', 3)   // The space between dots horizontally
            .attr('height', 3)  // The space between dots vertically
            .append('circle')
            .attr('cx', 1.5)  // Position of the circle inside the pattern (center it within the pattern box)
            .attr('cy', 1.5)  // Same for vertical positioning
            .attr('r', 0.5)   // Reduced radius for smaller dots
            .attr('fill', '#043f53');  // Color of the dots

        this.dotted = this.svg.append('g');
    }

    // Generate regions from country data
    generateRegions() {
        this.regions = this.countryData.reduce((acc, { Region, Country }) => {
            if (!acc[Region]) {
                acc[Region] = [];
            }
            acc[Region].push(Country);
            return acc;
        }, {});
    }

    // Load GeoJSON data
    loadData() {
        const path = this.path
        d3.json("./assets/js/json/geoJson.json").then((jsonData) => {
            this.data = jsonData;

            // Draw the dotted map
            this.dotted.selectAll("path")
                .data(this.data.features)
                .enter()
                .append("path")
                .attr("d", this.path)
                .attr("fill", "url(#dotted-pattern)")
                .attr("stroke", "#043f53")
                .attr("stroke-width", 0);

            // Create country group and set event listeners
            const countries = this.g.selectAll("g")
                .data(this.data.features)
                .enter()
                .append("g")
                .attr("cursor", "pointer")
                .attr("class", "countrygroup")
                .attr("id", d => `${Utils.modifyText(d.properties.name.toLowerCase())}`)

                .on("mouseover", (event, d) => {
                    if (window.innerWidth > 992) {
                        d3.select(".tooltip").remove();
                        const tooltip = d3.select(this.container).append("div")
                            .attr("class", "tooltip")
                            .style("position", "absolute")
                            .style("background", "rgba(0,0,0,0.6)")
                            .style("border", "2px solid #00A6D6")
                            .style("border-radius", "15px")
                            .style("pointer-events", "none")
                            .style("font-size", "12px")
                            .style("opacity", 0)
                            .style("backdrop-filter", "blur(100)");

                        const countryName = d.properties.name || "Unknown";
                        const region = this.getRegion(countryName);
                        const countryData = this.countryData.find(c => c.Country === countryName);
                        const total = countryData ? countryData.Total : "N/A";

                        const averages = Utils.getSegmentWiseAverage(countryData);

                        tooltip.style("opacity", 1)
                            .html(`
                            <p class="small_para">Click to proceed</p>
                            <div class="title-head">
                                <img src="assets/images/Flags/${countryData ? countryData.Flag : ""}" alt="" class="flag">
                                <p class="paragraph">${countryData ? countryData.Country : ""}</p>
                            </div>
                            <ul class="segment-list">
                                <li>
                                    <div class="context">
                                        <div class="dot"></div>
                                        <p class="paragraph">Labor and human capital</p>
                                    </div>
                                    <div class="score">$<span>${averages[0].value.toFixed(1)}</span></div> 
                                </li>
                                <li>
                                    <div class="context">
                                        <div class="dot"></div>
                                        <p class="paragraph">Physical capital</p>
                                    </div>
                                    <div class="score">$<span>${averages[1].value.toFixed(1)}</span></div> 
                                </li>
                                <li>
                                    <div class="context">
                                        <div class="dot"></div>
                                        <p class="paragraph">Social capital</p>
                                    </div>
                                    <div class="score">$<span>${averages[2].value.toFixed(1)}</span></div>    
                                </li>
                                <li>
                                    <div class="context">
                                        <div class="dot"></div>
                                        <p class="paragraph">Natural capital</p>
                                    </div>
                                    <div class="score">$<span>${averages[3].value.toFixed(1)}</span></div>   
                                </li>
                                <li>
                                    <div class="context">
                                        <div class="dot"></div>
                                        <p class="paragraph">Innovation and intangible capital</p>
                                    </div>
                                    <div class="score">$<span>${averages[4].value.toFixed(1)}</span></div>     
                                </li>
                                <li>
                                    <div class="context">
                                        <div class="dot"></div>
                                        <p class="paragraph">Institutions</p>
                                    </div>
                                    <div class="score">$<span>${averages[5].value.toFixed(1)}</span></div>     
                                </li>
                            </ul>
                            <div class="bottom-text">
                                <p class="paragraph">Total productivity potential</p>
                                <div class="total-score">$<span>${total.toFixed(1)}</span></div>   
                            </div>
                        `);

                        // Track mousemove event to update tooltip position
                        d3.select(this.container).on("mousemove", (event) => {
                            tooltip.style("left", `${event.pageX + 10}px`)
                                .style("top", `${event.pageY + 10}px`);
                        });
                    }
                })
                .on("mouseout", () => {
                    d3.select(".tooltip").remove();
                    d3.select(this.container).on("mousemove", null);
                }).each(function (d) {

                    
                    const countryGroup = d3.select(this);

                    countryGroup.append("path")
                        .attr("d", path)
                        .attr("fill", "#ccc")
                        .attr("stroke", "#333")

                    // Append circle at centroid
                    const centroid = path.centroid(d);

                    const dotArrangement = {
                        "Russian Federation": { x: centroid[0] - 154.9, y: centroid[1] + 45.9 },
                        "Canada": { x: centroid[0] + 0, y: centroid[1] + 62.9 },
                        "United States": { x: centroid[0] + 41, y: centroid[1] + 37 },
                        "Norway": { x: centroid[0] + 0, y: centroid[1] + 34.9 },
                        "Chile": { x: centroid[0] - 2.9, y: centroid[1] + 0 },
                        "Indonesia": { x: centroid[0] - 8.9, y: centroid[1] - 3.9 },
                        "Malaysia": { x: centroid[0] + 11, y: centroid[1] + 0 }
                    };


                    if(d.properties.name === 'United States'){
                        console.log("x: ", centroid[0], "y: " + centroid[1])
                    }

                    const position = dotArrangement[d.properties.name] || { x: centroid[0], y: centroid[1] };


                    countryGroup.append("circle")
                        .attr('class', 'hotspot')
                        .attr("cx", position.x)
                        .attr("cy", position.y)
                        .attr("r", 2)  // Adjust size as needed
                        .attr("fill", "red")
                    // .attr("stroke", "black"); 
                })




        }).catch(error => {
            console.error("Error loading GeoJSON data:", error);
        });
    }

    getRegion(countryName) {
        for (const [region, countries] of Object.entries(this.regions)) {
            if (countries.includes(countryName)) {
                return region;
            }
        }
        return "Unknown Region";
    }

    // Zoom functionality to a specific region
    getZoom(regionBounds, regionName) {
        const regionCountries = this.regions[regionName];
        if (!regionCountries) return;

        // Step 1: Get the country data for the countries in the region
        const regionFeatures = this.data.features.filter(d => regionCountries.includes(d.properties.name));

        // Step 2: Find the Total value for each country
        const countryDataMap = new Map(this.countryData.map(c => [c.Country, c.Total]));

        // Step 3: Create a color scale based on the Total values
        const totalValues = regionCountries.map(country => countryDataMap.get(country) || 0);

        // Optional: Normalize the total values if they range from tens to hundreds
        const maxValue = d3.max(totalValues);
        const minValue = d3.min(totalValues);

        // Normalize values: this could help if your values span a wide range
        const normalizedTotalValues = totalValues.map(value => (value - minValue) / (maxValue - minValue));

        // Define the color scale with normalized values
        const colorScale = d3.scaleSequential()
            .domain([0, 1])  // domain is now normalized between 0 and 1
            .interpolator(d3.interpolateRgb("#A6D6E6", "#026CCA"));  // Start at light shade and end at dark shade

        // Hide all countries initially
        this.g.selectAll("path")
            .style("visibility", "hidden")
            .style("pointer-events", "none");

        // Highlight the countries in the region
        this.g.selectAll('.hotspot')
            .data(regionFeatures, d => d.properties.name)
            .attr('fill', '#fff');

        // Step 4: Apply the color scale to the countries in the region
        this.g.selectAll("path")
            .data(regionFeatures, d => d.properties.name)
            .attr('data-slide', (d, i) => i)
            .style("visibility", "visible")
            .style("pointer-events", "all")
            .attr("fill-opacity", 0.8)
            .style("fill", (d) => {
                const countryTotal = countryDataMap.get(d.properties.name) || 0; // Get the Total value
                // Normalize the country value and apply the color scale
                const normalizedValue = (countryTotal - minValue) / (maxValue - minValue);
                return colorScale(normalizedValue);  // Apply the color scale with normalized value
            })
            .attr('stroke-width', 0.5)
            .style('stroke', (d) => {
                const countryTotal = countryDataMap.get(d.properties.name) || 0;
                const normalizedValue = (countryTotal - minValue) / (maxValue - minValue);
                return colorScale(normalizedValue);
            })
            .on('click', (event, d) => {
                if (window.innerWidth > 992) {
                    SubPage.getInstance().showCountryScoreCardPage();

                    if (this.countryScoreCard != null) {
                        this.countryScoreCard.updateCountryScore(d.properties.name)
                    }
                } else {
                    const index = this.findCountryIndex(regionCountries, d.properties.name)
                    window.mobCountryDetailPanel.swiper.slideTo(index)
                }
            })

        // Step 5: Zoom logic
        const [[x0, y0], [x1, y1]] = regionBounds;
        const dx = x1 - x0;
        const dy = y1 - y0;
        const x = (x0 + x1) / 2;
        const y = (y0 + y1) / 2;
        const scale = Math.max(1, Math.min(5, 0.9 / Math.max(dx / this.width, dy / this.height)));
        const translate = [this.width / 2 - scale * x, this.height / 2 - scale * y];

        if (regionName === "Oceania") {
            // Apply an offset to the translate values (adjust the horizontal offset)
            translate[0] -= window.innerWidth <= 992 ? 100 : 300; // Adjust this value based on your specific needs
        }

        this.currentTransform = d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale);
        this.svg.transition().duration(750).call(this.zoomBehavior.transform, this.currentTransform);

        if (window.innerWidth <= 992) {
            this.svg.on(".zoom", null); // Disable zoom on mobile after zooming
        }

        // Make the wrapper visible
        this.wrapper.style.opacity = '1';
        this.wrapper.style.visibility = 'visible';
        this.wrapper.style.pointerEvents = 'auto';
    }

    findCountryIndex(data, countryName) {
        const index = data.indexOf(countryName);
        return index !== -1 ? index : "Country not found"; // Returns index or a message if not found
    }

    // Zoom to a specific region
    zoomToRegion(regionName) {
        var regions;

        if (window.innerWidth <= 992) {
            // MOBILE
            regions = {
                "Africa": [[-20, -40], [55, 30]],
                "Central & Eastern Europe": [[10, 0], [55, 75]],
                "East Asia": [[95, 0], [170, 65]],
                "Middle East": [[5, -5], [80, 60]],
                "North America": [[-145, -10], [-55, 80]],
                "Northern Europe": [[-40, 20], [40, 85]],
                "Oceania": [[75, -60], [180, 10]],
                "South America": [[-90, -60], [-30, 15]],
                "South Asia": [[50, 0], [110, 45]],
                "Southeast Asia": [[90, -25], [150, 25]],
                "Southern Europe": [[-20, 25], [20, 55]],
                "Western Europe": [[-30, 30], [30, 60]],
            };
        } else {
            // DESKTOP
            regions = {
                "Africa": [[-10, -40], [40, 10]],
                "Central & Eastern Europe": [[20, 55], [50, 50]],
                "East Asia": [[50, 20], [160, 50]],
                "Middle East": [[30, 10], [70, 40]],
                "North America": [[-170, -10], [-20, 70]],
                "Northern Europe": [[-50, 30], [70, 80]],
                "Oceania": [[75, -60], [180, 10]],
                "South America": [[-90, -60], [-30, 15]],
                "South Asia": [[30, -10], [130, 50]],
                "Southeast Asia": [[80, -20], [170, 25]],
                "Southern Europe": [[-20, 25], [20, 55]],
                "Western Europe": [[-30, 30], [30, 60]],
            };
        }

        if (regions[regionName]) {
            const regionBounds = regions[regionName].map(this.projection);
            this.getZoom(regionBounds, regionName);
        }
    }

    resetZoom() {
        this.g.selectAll("path")
            .style("visibility", "hidden")
            .style("pointer-events", "none");

        // this.g.transition()
        //     .duration(750)
        //     .attr("transform", "translate(0,0)scale(1)");
        // this.dotted.transition()
        //     .duration(750)
        //     .attr("transform", "translate(0,0)scale(1)");

        // Animate visibility to hidden
        this.wrapper.style.opacity = '0';
        this.wrapper.style.visibility = 'hidden';
        this.wrapper.style.pointerEvents = 'none';
    }

    // Handle window resizing
    handleResize() {
        window.addEventListener("resize", () => {
            this.width = this.container.offsetWidth;
            this.height = this.container.offsetHeight;

            this.svg.attr("width", this.width).attr("height", this.height);
            this.projection.translate([this.width / 2, this.height / 2]);
            this.g.selectAll("path").attr("d", this.path);
        });
    }
}
